import React, { useMemo, useState } from 'react';
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  Platform,
  ScrollView,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { Image } from 'expo-image';
import * as ImagePicker from 'expo-image-picker';
import { useTheme } from '../context/ThemeContext';
import { getSupabaseClient } from '../utils/supabase';
import { uploadStudentSignatureImage } from '../utils/studentSignatures';

const LIGHT_CREAM = '#faf8f4';

function guessMimeFromName(name) {
  const lower = String(name).toLowerCase();
  if (lower.endsWith('.png')) return 'image/png';
  if (lower.endsWith('.webp')) return 'image/webp';
  if (lower.endsWith('.gif')) return 'image/gif';
  if (lower.endsWith('.heic')) return 'image/heic';
  if (lower.endsWith('.jpeg')) return 'image/jpeg';
  return 'image/jpeg';
}

async function uriToArrayBuffer(uri) {
  const res = await fetch(uri);
  if (!res.ok) throw new Error(`Could not read image (${res.status})`);
  return res.arrayBuffer();
}

function studentLabel(student) {
  const name = String(student?.student_name || '').trim();
  if (name) return name;
  return String(student?.username || 'Student').trim();
}

export default function ClassBookSignaturesModal({
  visible,
  onClose,
  students = [],
  onSaved,
}) {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [showStudentDropdown, setShowStudentDropdown] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const selectedStudent = useMemo(
    () => students.find((s) => Number(s.id) === Number(selectedStudentId)) ?? null,
    [students, selectedStudentId]
  );

  const resetForm = () => {
    setSelectedStudentId(null);
    setShowStudentDropdown(false);
    setPendingFile(null);
    setError('');
  };

  const handleClose = () => {
    if (saving) return;
    resetForm();
    onClose?.();
  };

  const handlePickImage = async () => {
    setError('');
    try {
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== 'granted') {
        Alert.alert('Permission needed', 'Allow photo library access to upload a signature.');
        return;
      }
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: false,
        quality: 1,
      });
      if (result.canceled || !result.assets?.[0]) return;
      const asset = result.assets[0];
      setPendingFile({
        uri: asset.uri,
        fileName: asset.fileName || `signature_${Date.now()}.png`,
      });
    } catch (e) {
      setError(e?.message || 'Could not pick image');
    }
  };

  const handleSave = async () => {
    if (!selectedStudent || !pendingFile?.uri) {
      setError('Select a student and choose an image');
      return;
    }
    const supabase = getSupabaseClient();
    if (!supabase) {
      setError('Storage is not configured');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const displayName = studentLabel(selectedStudent);
      const result = await uploadStudentSignatureImage(supabase, displayName, {
        uri: pendingFile.uri,
        fileName: pendingFile.fileName,
        uriToArrayBuffer,
        guessMimeFromName,
      });
      onSaved?.({ studentName: displayName, url: result.url });
      resetForm();
      onClose?.();
    } catch (e) {
      setError(e?.message || 'Upload failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={handleClose}>
      <View style={styles.overlay}>
        <Pressable style={styles.backdrop} onPress={handleClose} />
        <View
          style={styles.sheet}
          {...(Platform.OS === 'web' ? { dataSet: { classBookSignaturesModal: 'true' } } : {})}
        >
          <Text style={styles.title}>Signatures</Text>

          <Text style={styles.label}>Select student</Text>
          <TouchableOpacity
            style={styles.dropdownTrigger}
            onPress={() => setShowStudentDropdown((open) => !open)}
            disabled={saving}
            {...(Platform.OS === 'web' ? { dataSet: { classBookSignaturesStudentTrigger: 'true' } } : {})}
          >
            <Text
              style={[
                styles.dropdownTriggerText,
                !selectedStudent && styles.dropdownPlaceholder,
              ]}
              numberOfLines={1}
            >
              {selectedStudent ? studentLabel(selectedStudent) : 'Select student'}
            </Text>
            <Text style={styles.dropdownArrow}>{showStudentDropdown ? '▲' : '▼'}</Text>
          </TouchableOpacity>

          {showStudentDropdown ? (
            <ScrollView style={styles.dropdownList} nestedScrollEnabled keyboardShouldPersistTaps="handled">
              {students.map((student) => (
                <TouchableOpacity
                  key={student.id}
                  style={[
                    styles.dropdownRow,
                    Number(selectedStudentId) === Number(student.id) && styles.dropdownRowSelected,
                  ]}
                  onPress={() => {
                    setSelectedStudentId(student.id);
                    setShowStudentDropdown(false);
                  }}
                >
                  <Text style={styles.dropdownRowText}>{studentLabel(student)}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          ) : null}

          <TouchableOpacity
            style={styles.uploadBtn}
            onPress={() => void handlePickImage()}
            disabled={saving}
            {...(Platform.OS === 'web' ? { dataSet: { classBookSignaturesUploadBtn: 'true' } } : {})}
          >
            <Text style={styles.uploadBtnText}>
              {pendingFile ? `Selected: ${pendingFile.fileName}` : 'Upload file'}
            </Text>
          </TouchableOpacity>

          {pendingFile?.uri ? (
            <Image
              source={{ uri: pendingFile.uri }}
              style={styles.preview}
              contentFit="contain"
            />
          ) : null}

          {error ? <Text style={styles.errorText}>{error}</Text> : null}

          <View style={styles.actionsRow}>
            <TouchableOpacity
              style={styles.cancelBtn}
              onPress={handleClose}
              disabled={saving}
            >
              <Text style={styles.cancelBtnText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.saveBtn, saving && styles.saveBtnDisabled]}
              onPress={() => void handleSave()}
              disabled={saving || !selectedStudent || !pendingFile}
            >
              {saving ? (
                <ActivityIndicator size="small" color="#111" />
              ) : (
                <Text style={styles.saveBtnText}>Save</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const getStyles = (theme) =>
  StyleSheet.create({
    overlay: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 20,
    },
    backdrop: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: 'rgba(0,0,0,0.45)',
    },
    sheet: {
      width: '100%',
      maxWidth: 420,
      borderRadius: 12,
      padding: 20,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
      borderWidth: 1,
      borderColor: theme.colors.border,
    },
    title: {
      fontSize: 20,
      fontWeight: '700',
      color: theme.colors.heading,
      marginBottom: 16,
      backgroundColor: 'transparent',
    },
    label: {
      fontSize: 15,
      fontWeight: '600',
      color: theme.colors.text,
      marginBottom: 8,
      backgroundColor: 'transparent',
    },
    dropdownTrigger: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderWidth: 1,
      borderColor: theme.colors.border,
      borderRadius: 8,
      paddingVertical: 12,
      paddingHorizontal: 12,
      marginBottom: 8,
      backgroundColor: theme.isDarkMode ? theme.colors.inputBackground || theme.colors.surface : LIGHT_CREAM,
    },
    dropdownTriggerText: {
      flex: 1,
      fontSize: 16,
      color: theme.colors.text,
      marginRight: 8,
      backgroundColor: 'transparent',
    },
    dropdownPlaceholder: {
      opacity: 0.55,
    },
    dropdownArrow: {
      fontSize: 12,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    dropdownList: {
      maxHeight: 160,
      borderWidth: 1,
      borderColor: theme.colors.border,
      borderRadius: 8,
      marginBottom: 12,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    },
    dropdownRow: {
      paddingVertical: 10,
      paddingHorizontal: 12,
      backgroundColor: 'transparent',
    },
    dropdownRowSelected: {
      backgroundColor: theme.isDarkMode ? 'rgba(212,175,55,0.18)' : 'rgba(212,175,55,0.22)',
    },
    dropdownRowText: {
      fontSize: 15,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    uploadBtn: {
      marginTop: 8,
      marginBottom: 12,
      paddingVertical: 12,
      paddingHorizontal: 14,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.colors.border,
      backgroundColor: theme.isDarkMode ? theme.colors.inputBackground || theme.colors.surface : LIGHT_CREAM,
    },
    uploadBtnText: {
      fontSize: 15,
      fontWeight: '600',
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    preview: {
      width: '100%',
      height: 80,
      marginBottom: 12,
      backgroundColor: 'transparent',
    },
    errorText: {
      color: '#c62828',
      fontSize: 13,
      marginBottom: 8,
      backgroundColor: 'transparent',
    },
    actionsRow: {
      flexDirection: 'row',
      gap: 12,
      marginTop: 8,
    },
    cancelBtn: {
      flex: 1,
      paddingVertical: 12,
      borderRadius: 8,
      alignItems: 'center',
      backgroundColor: '#999999',
    },
    cancelBtnText: {
      color: '#FFFFFF',
      fontSize: 16,
      fontWeight: '600',
      backgroundColor: 'transparent',
    },
    saveBtn: {
      flex: 1,
      paddingVertical: 12,
      borderRadius: 8,
      alignItems: 'center',
      backgroundColor: theme.colors.primary || '#d4af37',
    },
    saveBtnDisabled: {
      opacity: 0.65,
    },
    saveBtnText: {
      color: '#111',
      fontSize: 16,
      fontWeight: '700',
      backgroundColor: 'transparent',
    },
  });
